import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Dropdown, Form } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { BsArrowRepeat, BsDownload, BsJournalBookmark } from 'react-icons/bs';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import { EngineIcon } from 'Utils';
import { getSourceNomenclature } from 'Utils/nomenclature';
import ProviderBadge from 'Secure/ProviderBadge';
import HealthcareBadge from 'Secure/HealthcareBadge';
import InventorySelector, { InventoryNameModal } from 'Secure/InventorySelector';
import InventoryFieldsAccordion from './MaskDialog/InventoryFieldsAccordion';
import FileInventoryGroups from './MaskDialog/FileInventoryGroups';
import InventoryIssuesBanner from './InventoryIssuesBanner';
import TargetComparisonBanner from './TargetComparisonBanner';
import ChangeProviderDialog from './ChangeProviderDialog';
import { collectInventoryIssues, registerAlgorithmCatalog } from './uniquenessWarning';
import {
  applyTargetGaps,
  dropTargetMissing,
  describeReport,
  restoreTargetMissing,
  tableExtrasFromInventoryContent,
  tableExtrasFromReport,
  TERMINAL_JOB_STATUSES,
} from './targetComparison';
import { buildInlineFileInventory, dropUnmaskableFiles } from './fileInventoryUtils';
import './MaskDialog/InventoryPage.css';
import {
  listDataSourceInventories,
  createDataSourceInventory,
  updateInventory,
  deleteInventory,
  inventoryContentOf,
  latestInventoryOf,
  inventoryToSelectedFields,
  getMaskingEntityAlgorithmMap,
  getMaskingProviders,
  listMaskingAlgorithms,
  selectedFieldsToInventory,
  startMaskingJob,
  compareInventoryTargets,
  deriveInventoryForTarget,
  getProfileJob,
  isDerivedInventory,
} from 'Secure/api';
import {
  discoverTablesToFileTableMeta,
  discoverTablesToMaskingSelectedFields,
  loadDiscoverOverviewTables,
  searchDiscoverIndexTables,
} from 'Secure/secureDiscoverFields';
import { getSelectedFieldsCount } from 'Secure/Utils';
import {
  mergeMaskingInventoryWithDiscover,
  attachRecommendations,
  clearAllSelections,
  resetAllToRecommended,
  ensureMaskingFieldShape,
} from './inventoryMerge';
import AppContext from 'Utils/AppContext';
import { getDataSourceNumericId, isFileDatasourceType, isEligibleMaskTarget } from 'DataSources/datasourcesApi';
import { SourcePickerField, RULES } from 'DataSources/SourcePicker';
import { ensureConnectionTested } from 'Utils/connectionGate';
import { exportInventoryFields } from 'Secure/inventoryExport';

/**
 * Prefer the server's structured error message over the generic "POST /api/... failed: 409" that
 * ApiError.message carries. The API returns { code, message, ... } in the response body (ApiError.body);
 * for a 409 JOB_ALREADY_ACTIVE that message names the blocking job, e.g.
 * "Masking job #15 (sandbox_facets_sqlserver) is running".
 */
/** Poll cadence while a target's Profile runs before the comparison can happen. */
const PROFILE_POLL_MS = 5000;

/** Worker-reported reason a Profile job ended FAILED / INTERRUPTED, for the target banner. */
function profileJobError(job) {
  if (!job) return '';
  const cached = job.cachedStatusJson;
  if (cached) {
    try {
      const parsed = typeof cached === 'string' ? JSON.parse(cached) : cached;
      if (parsed && parsed.message) return String(parsed.message);
    } catch (e) {
      // fall through to the raw fields
    }
  }
  return String(job.upstreamErrorBody || job.errorCode || '');
}

function apiErrorMessage(e) {
  const serverMsg = e && e.body && typeof e.body === 'object' ? e.body.message : null;
  if (serverMsg && String(serverMsg).trim()) {
    return String(serverMsg);
  }
  return (e && e.message) || String(e);
}

/**
 * The reusable masking-inventory editor body: source header (+ optional masking-engine
 * selector), the no-discovery warning, field counts, and the fields table. The host owns
 * persistence and the footer (Mask/Cancel here; a future standalone manager would supply
 * buffered Save/Cancel) — this component is purely the editor surface. `showRecommendation`
 * is hard-on here: the inventory editor always compares against a saved inventory, so the
 * RECOMMENDED column and the MASK-right layout apply.
 */
/**
 * Two-row inventory title, left-aligned. Row 1: engine icon + name + engine. Row 2: # fields
 * selected. Rendered ABOVE the scroll body so it stays visible; the accordion's filter row sticks
 * to the top of the scroll body below it. The right column carries the inventory picker (a source
 * can have many named inventories) and the provider badge (the provider itself is changed from
 * the page-header Actions menu → Change Provider).
 */
function InventoryTitle({
  connection,
  engine,
  providers,
  selectedProvider,
  noDiscoverFields,
  selectedCount,
  countNoun = 'masking',
  inventorySelector,
  healthcareTag = false,
  healthcareTagSource = '',
}) {
  return (
    <div className='inv-title'>
      <div className='d-flex align-items-start justify-content-between'>
        <div>
          <div className='inv-title-name'>
            <EngineIcon classes='mr-2' engine={engine} /> {connection}
          </div>
          <div className='inv-title-count'>
            <span className='accent'>{selectedCount}</span> fields selected for {countNoun}
            {engine ? <span className='inv-title-sub text-uppercase ml-2'>{engine} database</span> : null}
          </div>
        </div>
        <div className='d-flex flex-column align-items-end' style={{ gap: '0.4rem' }}>
          {inventorySelector}
          <HealthcareBadge healthcareTag={healthcareTag} healthcareTagSource={healthcareTagSource} />
          <ProviderBadge providers={providers} serviceId={selectedProvider} defaultLabel='C2 Masking' />
        </div>
      </div>
      {noDiscoverFields && (
        <Alert variant='warning' className='mt-2 mb-0'>
          No fields loaded from discovery for this data source. Run a completed discover job first; masking
          inventory is built from discovery results only.
        </Alert>
      )}
    </div>
  );
}

function InventoryPage() {
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const dataSourceIdParam = Number(routeId);
  const [error, setError] = useState(null);
  const [selectedFields, setSelectedFields] = useState({});
  const [fetchingData, setFetchingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [algorithms, setAlgorithms] = useState([]);
  const [noDiscoverFields, setNoDiscoverFields] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [currentEntityMap, setCurrentEntityMap] = useState({});
  // Table-index (PeopleSoft-80K) runs: { runId, totalTables, loadedCount } when the
  // discovery overview was loaded from the paged index; null for classic runs. Drives
  // the accordion's server-search fallback when the load was capped.
  const [indexInfo, setIndexInfo] = useState(null);
  const [inventories, setInventories] = useState([]);
  // undefined = not yet resolved (first load picks nav target / latest); null = new unsaved draft.
  const [selectedInventoryId, setSelectedInventoryId] = useState(undefined);
  // Name prompt for creating an inventory from the current (discover-seeded) draft. pendingAction
  // remembers whether Save or Mask triggered it so the flow resumes after the name is supplied.
  const [nameModal, setNameModal] = useState({ open: false, pendingAction: null });

  const { setNotification, connections, isReadOnly } = useContext(AppContext);
  const source = Number.isFinite(dataSourceIdParam)
    ? (connections || []).find((c) => c?.id === dataSourceIdParam)
    : null;
  const connection = source?.name || '';
  const engine = source ? source.type : '';
  const dataSourceId = getDataSourceNumericId(source);
  // File sources render the folder/file-grouped inventory body with per-file and
  // per-folder mask actions (single inline-inventory jobs) instead of the accordion.
  const isFileSource = isFileDatasourceType(source?.datasourceType);
  // Confirmed uniqueness conflicts for the red banner — recomputed from the same descriptors
  // the accordion rows render, so the banner and the per-row icons never disagree. `algorithms`
  // is a dependency because loading the catalog registers which algorithms the worker declares
  // uniqueness-safe (registerAlgorithmCatalog), which changes the verdict for custom algorithms.
  const inventoryIssues = useMemo(
    () => (isFileSource ? [] : collectInventoryIssues(selectedFields)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFields, isFileSource, algorithms]
  );
  const nomenclature = getSourceNomenclature(engine, source?.datasourceType);
  const preferredDiscoverRunId = location.state?.prevSearchid;
  // Opened from the Secure landscape masking history: view inventory without mutating
  // (no autosave) and without the ability to launch a masking job. A read-only account always
  // gets this view.
  const readOnly = location.state?.readOnly === true || !!isReadOnly;

  // Connection-level lock (distinct from the view-only `readOnly` route state below): a
  // read-only source can't be masked in place, but CAN be masked cross-source into a
  // writable target — the server's read-only check applies to the mutation target only.
  const sourceIsReadOnly = source?.readOnly === true;

  // Cross-source masking: '' = classic in-place masking; otherwise the numeric id of a
  // DIFFERENT data source that receives the masked data (INSERT strategy). This source
  // stays the source of record — its inventory drives the job either way.
  const [targetDataSourceId, setTargetDataSourceId] = useState('');
  const [truncateBeforeInsert, setTruncateBeforeInsert] = useState(false);
  // Target comparison (server TargetComparisonReport) for the selected mask target, plus the
  // in-flight compare call. Picking a target runs a READ-ONLY compare of the staged fields with
  // the target's profiled schema: the report drives the banner and greys out the "not on target"
  // rows, but nothing is persisted — the trimmed `target_<name>` inventory is created only when
  // the user clicks Save or Mask. Kept in a ref too so the load effect can reuse it without
  // re-fetching.
  const [targetReport, setTargetReport] = useState(null);
  const targetReportRef = useRef(null);
  useEffect(() => {
    targetReportRef.current = targetReport;
  }, [targetReport]);
  const [deriving, setDeriving] = useState(false);
  const currentInventory =
    selectedInventoryId != null
      ? (inventories || []).find((i) => i && i.id === selectedInventoryId) || null
      : null;
  const currentIsDerived = isDerivedInventory(currentInventory);
  const targetNameOf = (id) =>
    (connections || []).find(
      (c) => getDataSourceNumericId({ id: c.id, tableData: { id: c.id } }) === id
    )?.name || `data source #${id}`;
  // Mongo INSERT reruns are only idempotent when the target is truncated first: copied
  // documents keep their source _id, so a rerun without truncate dup-keys (E11000) on the
  // very first batch. Default the toggle ON for mongo sources — the value is always sent
  // explicitly, so the masking-service default never applies to UI-started jobs. The user
  // can still opt out; applied once so their choice is never overridden.
  const truncateDefaultApplied = useRef(false);
  useEffect(() => {
    if (!truncateDefaultApplied.current && String(engine).toLowerCase() === 'mongodb') {
      truncateDefaultApplied.current = true;
      setTruncateBeforeInsert(true);
    }
  }, [engine]);
  // Eligible targets: the shared mask-target rule (isEligibleMaskTarget) — writable DB sources
  // other than this one, on the SAME engine (the cross-source INSERT path replays source-shaped
  // DDL/DML), no dynamodb / Salesforce / file / platform-managed rows, and never a duplicate row
  // pointing at this source's own database and schema. Another row on the same host, or the
  // same database with a different schema, is a legitimate target.
  const targetRules = useMemo(() => (source ? [RULES.maskTargetFor(source)] : []), [source]);
  const eligibleTargets = source ? (connections || []).filter((c) => isEligibleMaskTarget(source, c)) : [];

  const selectedFieldsRef = useRef(selectedFields);
  // Per-table routing metadata (sniffedType/delimiter) from the discovery overview —
  // attached to inventory table objects at persist time; not user-editable state.
  const fileTableMetaRef = useRef({});
  // Target-derived inventories only: per-table `sourceColumns` (INSERT copy restriction) read
  // from the saved content, refreshed from the latest comparison report. Re-attached at persist
  // time so a Save/Mask round-trip through the editor never drops it.
  const derivedTableExtrasRef = useRef({});
  // The fields as they were when the current target's gap overlay was applied — lets clearing or
  // changing the target restore the greyed-out rows, and lets the ROOT inventory persist without
  // the preview-only overlay baked in. Null when no overlay is staged.
  const preTargetSnapshotRef = useRef(null);
  // Target picked while a derived inventory was open: compare after the root reloads.
  const pendingTargetRef = useRef(null);

  useEffect(() => {
    selectedFieldsRef.current = selectedFields;
  }, [selectedFields]);

  // Change Provider dialog (page-header Actions menu).
  const [providerDialog, setProviderDialog] = useState(false);
  const handleProviderChange = async (serviceId) => {
    setProviderDialog(false);
    setSelectedProvider(serviceId);
    try {
      // Pass the source id so a healthcare-tagged source gets the safe-harbor-overlaid map.
      const map = await getMaskingEntityAlgorithmMap(serviceId, dataSourceId).catch(() => ({}));
      const entityMap = map && typeof map === 'object' ? map : {};
      setCurrentEntityMap(entityMap);
      // A provider switch invalidates the existing inventory — reset every field to the new
      // provider's recommended algorithm. Staged in the UI only; persisted on Mask/Save.
      setSelectedFields((prev) => resetAllToRecommended(prev, entityMap));
    } catch (e) {
      // keep current map on failure
    }
  };

  useEffect(() => {
    const loadInventory = async () => {
      if (!dataSourceId) return;
      setFetchingData(true);
      setError(null);
      setNoDiscoverFields(false);
      // A freshly loaded inventory has no staged target overlay.
      preTargetSnapshotRef.current = null;
      try {
        const [algoList, providerList, inventoryList] = await Promise.all([
          listMaskingAlgorithms().catch(() => []),
          getMaskingProviders().catch(() => []),
          listDataSourceInventories(dataSourceId).catch(() => []),
        ]);
        setAlgorithms(algoList);
        registerAlgorithmCatalog(algoList);
        setProviders(providerList);
        setInventories(inventoryList);

        // First load: resolve which inventory to edit — the one handed over by navigation state,
        // else the most recently updated, else a new draft (null). Setting the id re-runs this
        // effect, which then loads that inventory's content.
        if (selectedInventoryId === undefined) {
          const navId = location.state?.inventoryId;
          const navInv = navId != null ? inventoryList.find((inv) => inv && inv.id === navId) : null;
          const latest = latestInventoryOf(inventoryList);
          setSelectedInventoryId(navInv ? navInv.id : latest ? latest.id : null);
          return;
        }

        const selectedInventory =
          selectedInventoryId != null
            ? inventoryList.find((inv) => inv && inv.id === selectedInventoryId) || null
            : null;

        // Resolve the effective provider FIRST (inventory preference → registry default → first),
        // then load THAT provider's entity→algorithm map so the displayed algorithms match the
        // selected provider on initial render (not just after a manual switch).
        const preferredId = selectedInventory?.preferredMaskingProviderId;
        const preferred = preferredId && providerList.find((p) => p.serviceId === preferredId);
        const defaultProv = providerList.find((p) => p.isDefault);
        const effectiveProvider =
          (preferred && preferred.serviceId)
          || (defaultProv && defaultProv.serviceId)
          || (providerList[0]?.serviceId || null);
        setSelectedProvider(effectiveProvider);
        // Pass the source id so a healthcare-tagged source gets the safe-harbor-overlaid map
        // on the initial render (not just after a manual provider switch).
        const map = await getMaskingEntityAlgorithmMap(effectiveProvider || undefined, dataSourceId).catch(() => ({}));
        const entityMap = map && typeof map === 'object' ? map : {};
        setCurrentEntityMap(entityMap);

        const selectedFromInventory = inventoryToSelectedFields(inventoryContentOf(selectedInventory));

        setIndexInfo(null);
        const overviewTables = await loadDiscoverOverviewTables(dataSourceId, preferredDiscoverRunId, {
          onWarning: (notification) => setNotification(notification),
          onIndexInfo: (info) => setIndexInfo(info),
        });
        fileTableMetaRef.current = discoverTablesToFileTableMeta(overviewTables);

        const discoverMap = discoverTablesToMaskingSelectedFields(overviewTables);
        const merged = mergeMaskingInventoryWithDiscover(selectedFromInventory, discoverMap, entityMap);

        // A target-derived inventory re-applies its target's gaps: the merge above unions
        // discovery back in, so tables/columns the derived copy dropped would otherwise
        // resurrect as unsaved rows. Reuse the report when it is for this target; else fetch it
        // (the compare also applies the profile-freshness rule and may come back pending).
        let overlay = merged;
        derivedTableExtrasRef.current = isDerivedInventory(selectedInventory)
          ? tableExtrasFromInventoryContent(inventoryContentOf(selectedInventory))
          : {};
        if (isDerivedInventory(selectedInventory)) {
          const tId = selectedInventory.targetDataSourceId;
          setTargetDataSourceId(String(tId));
          let report =
            targetReportRef.current && targetReportRef.current.targetDataSourceId === tId
              ? targetReportRef.current
              : null;
          if (!report) {
            try {
              const cmp = await compareInventoryTargets({
                dataSourceId,
                inventoryId: selectedInventory.derivedFromInventoryId,
                targetDataSourceIds: [tId],
              });
              report = cmp && Array.isArray(cmp.reports) ? cmp.reports[0] || null : null;
            } catch (cmpErr) {
              report = null;
            }
            setTargetReport(report);
          }
          if (report) {
            overlay = applyTargetGaps(merged, report);
            const fromReport = tableExtrasFromReport(report);
            if (Object.keys(fromReport).length > 0) derivedTableExtrasRef.current = fromReport;
          }
        }

        if (Object.keys(merged).length > 0) {
          setSelectedFields(overlay);
        } else {
          // No discovery fields at all — seed from whatever discover returned (typically empty)
          // and surface the warning. attachRecommendations fills the display-only column.
          setSelectedFields(attachRecommendations(ensureMaskingFieldShape(discoverMap), entityMap));
          setNoDiscoverFields(true);
        }
        // A target picked while a derived inventory was open: the root is loaded now — compare.
        if (pendingTargetRef.current != null && !isDerivedInventory(selectedInventory)) {
          const pendingTarget = pendingTargetRef.current;
          pendingTargetRef.current = null;
          await compareWithTarget(pendingTarget, overlay);
        }
      } catch (e) {
        setError(e?.message || 'Unable to load inventory');
      } finally {
        setFetchingData(false);
      }
    };
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceId, preferredDiscoverRunId, selectedInventoryId]);

  // Inventory edits (including a provider switch and "Accept all") are staged in the UI
  // only. Nothing persists until the user explicitly clicks Save or Mask.

  // Apply the current provider's recommended algorithm to every field across all tables. Staged
  // only; persisted on Save/Mask.
  const handleUseAllRecommended = () => {
    setSelectedFields((prev) => resetAllToRecommended(prev, currentEntityMap));
  };

  // Deselect everything: no field checked, no algorithm — saving/masking from here masks nothing.
  // Staged only; persisted on Save/Mask.
  const handleClearAll = () => {
    setSelectedFields((prev) => clearAllSelections(prev));
  };

  // Server-search fallback for capped index-mode loads: fetch tables matching the query
  // from the WHOLE run and stage any that aren't loaded yet. Merged tables get the same
  // shape as an initial load (recommendation-seeded, saved:false) so they behave exactly
  // as if they'd been inside the cap all along. Tables already present are never touched
  // (repeat queries are idempotent; staged edits survive).
  const handleServerSearch = useCallback(async (q) => {
    if (!indexInfo) return;
    const { tables } = await searchDiscoverIndexTables(indexInfo.runId, q);
    if (!tables.length) return;
    const shaped = mergeMaskingInventoryWithDiscover(
      {},
      discoverTablesToMaskingSelectedFields(tables),
      currentEntityMap
    );
    setSelectedFields((prev) => {
      let added = false;
      const next = { ...prev };
      Object.keys(shaped).forEach((tk) => {
        if (!next[tk]) {
          next[tk] = shaped[tk];
          added = true;
        }
      });
      return added ? next : prev;
    });
  }, [indexInfo, currentEntityMap]);

  const serverSearch = useMemo(
    () =>
      indexInfo && indexInfo.totalTables > indexInfo.loadedCount
        ? { totalTables: indexInfo.totalTables, onSearch: handleServerSearch }
        : null,
    [indexInfo, handleServerSearch]
  );

  const refreshInventories = async () => {
    const list = await listDataSourceInventories(dataSourceId).catch(() => []);
    setInventories(list);
    return list;
  };

  /**
   * Persist the staged fields AND the chosen provider onto the inventory. Existing inventory →
   * partial PUT (blank provider id clears it back to the platform default). Draft → POST to the
   * collection endpoint with the user-supplied name. Returns the saved inventory id.
   */
  const persistInventory = async (name) => {
    // Saved inventories must never carry entries for file formats masking can't handle —
    // they're shown grayed-out in the editor but are dropped at persist time. Maskability
    // keys on each file's content-sniffed format from discovery (extension fallback for
    // runs without sampler_type).
    const baseFields = isFileSource
      ? dropUnmaskableFiles(selectedFieldsRef.current, source?.datasourceType, fileTableMetaRef.current)
      : selectedFieldsRef.current;
    // A target-derived inventory never persists rows its target lacks (they render greyed-out
    // as "not on target" but must not round-trip into the saved copy). A ROOT inventory is the
    // opposite: the target overlay is preview-only, so its greyed-out rows are restored to their
    // pre-overlay state before persisting — the root keeps the full field set.
    const stagedFields = currentIsDerived
      ? dropTargetMissing(baseFields)
      : restoreTargetMissing(baseFields, preTargetSnapshotRef.current);
    const content = selectedFieldsToInventory(
      stagedFields, connection, 'ui-user',
      {
        fileSource: isFileSource,
        tableMeta: fileTableMetaRef.current,
        ...(currentIsDerived ? { tableExtras: derivedTableExtrasRef.current } : {}),
      }
    );
    if (selectedInventoryId != null) {
      await updateInventory(selectedInventoryId, {
        content,
        preferredMaskingProviderId: selectedProvider || '',
      });
      await refreshInventories();
      return selectedInventoryId;
    }
    const created = await createDataSourceInventory(dataSourceId, {
      name,
      content,
      ...(selectedProvider ? { preferredMaskingProviderId: selectedProvider } : {}),
    });
    const newId = created && created.id != null ? created.id : null;
    await refreshInventories();
    if (newId != null) setSelectedInventoryId(newId);
    return newId;
  };

  const doSave = async (name) => {
    setSubmitting(true);
    try {
      const savedId = await persistInventory(name);
      // A target preview on a ROOT inventory materialises here: derive-for-target creates (or
      // regenerates) the trimmed `target_<name>` inventory the compare previewed. Nothing was
      // persisted for the target before this click.
      const targetId = targetDataSourceId !== '' ? Number(targetDataSourceId) : null;
      let body = `Saved masking inventory for ${connection}`;
      if (targetId != null && !currentIsDerived && savedId != null) {
        const res = await deriveInventoryForTarget(savedId, targetId);
        if (res && res.report) setTargetReport(res.report);
        if (res && res.inventory && res.inventory.name) {
          body = `Saved ${connection} and ${res.created ? 'created' : 'regenerated'} ${res.inventory.name} for ${targetNameOf(targetId)}`;
        }
      }
      setNotification({ header: 'Inventory saved', body });
      history.push('/secure/mask');
    } catch (e) {
      setNotification({ header: 'Save failed', body: e?.message || String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const doMask = async (name) => {
    setSubmitting(true);
    try {
      // Provider routing comes from the inventory's own preferredMaskingProviderId (just
      // persisted above) — no explicit registeredServiceId override, so a stale preference
      // degrades to the registry default instead of failing the start.
      const targetId = targetDataSourceId !== '' ? Number(targetDataSourceId) : null;
      // The target comparison already knows this run cannot succeed — say why instead of
      // letting the server 422.
      if (targetId != null && targetReport && targetReport.targetDataSourceId === targetId) {
        if (targetReport.profilePending) {
          setNotification({ header: 'Target fingerprint in progress', body: describeReport(targetReport, targetNameOf(targetId)) });
          return;
        }
        if (targetReport.snapshotMissing) {
          setNotification({ header: 'Target has no fingerprinted schema', body: describeReport(targetReport, targetNameOf(targetId)) });
          return;
        }
        if (targetReport.nothingToMask) {
          setNotification({ header: 'Nothing to mask on target', body: describeReport(targetReport, targetNameOf(targetId)) });
          return;
        }
      }
      const inventoryId = await persistInventory(name);
      // A target preview on a ROOT inventory materialises here (first persist since the compare):
      // derive the trimmed `target_<name>` inventory and run the job against IT, not the root.
      let maskInventoryId = inventoryId;
      if (targetId != null && !currentIsDerived && inventoryId != null) {
        const res = await deriveInventoryForTarget(inventoryId, targetId);
        const report = res && res.report ? res.report : null;
        if (report) setTargetReport(report);
        if (report && (report.profilePending || report.snapshotMissing || report.nothingToMask)) {
          setNotification({
            header: report.profilePending
              ? 'Target fingerprint in progress'
              : report.snapshotMissing
              ? 'Target has no fingerprinted schema'
              : 'Nothing to mask on target',
            body: describeReport(report, targetNameOf(targetId)),
          });
          return;
        }
        if (res && res.inventory && res.inventory.id != null) maskInventoryId = res.inventory.id;
      }
      // Pre-run gate (after persist, so edits survive the redirect): source — and the
      // cross-source target when one is picked — need a successful connection test.
      // On failure the helper routes to the source editor to fix the connection.
      const gate = await ensureConnectionTested(dataSourceId, {
        history,
        setNotification,
        name: connection,
      });
      if (!gate.ok) return;
      if (targetId != null) {
        const targetGate = await ensureConnectionTested(targetId, { history, setNotification });
        if (!targetGate.ok) return;
      }
      await startMaskingJob({
        dataSourceId,
        inventoryId: maskInventoryId,
        ...(targetId != null
          ? { targetDataSourceId: targetId, writingStrategy: 'INSERT', truncateBeforeInsert }
          : {}),
      });
      const targetName =
        targetId != null
          ? (connections || []).find(
              (c) => getDataSourceNumericId({ id: c.id, tableData: { id: c.id } }) === targetId
            )?.name || `data source #${targetId}`
          : null;
      setNotification({
        header: 'Running masking',
        body: targetName
          ? `Successfully started masking job for ${connection} → ${targetName}`
          : `Successfully started masking job for ${connection}`
      });
      // Job started: land on the Secure Data Landscape (job status lives there), not the
      // legacy masking-sources list.
      history.push('/secure');
    } catch (e) {
      // 422 = the cross-source pre-flight rejected the target: it is missing tables/columns the
      // inventory references (the structured report rides on the error body), or its Profile
      // was just started (TARGET_PROFILE_PENDING — the banner waits it out and re-compares).
      const body = e && e.body && typeof e.body === 'object' ? e.body : null;
      const preflight = targetDataSourceId !== '' && e && e.status === 422;
      if (preflight && body && body.code === 'TARGET_PROFILE_PENDING') {
        const tId = Number(targetDataSourceId);
        const profileJobId = body.profileJobId != null ? body.profileJobId : body.extra && body.extra.profileJobId;
        setTargetReport({
          targetDataSourceId: tId,
          targetName: targetNameOf(tId),
          profilePending: true,
          profileJobId,
        });
        setNotification({ header: 'Target fingerprint in progress', body: apiErrorMessage(e) });
        return;
      }
      const report = body && (body.report || (body.extra && body.extra.report));
      if (preflight && report && typeof report === 'object') setTargetReport(report);
      setNotification({
        header: preflight ? 'Target failed pre-flight check' : 'Masking failed',
        body: preflight
          ? `${apiErrorMessage(e)} — pick the target again to re-compare, or pick a different target.`
          : apiErrorMessage(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Mask a subset of the inventory (one file or one folder's files) as a single job.
   * The FULL inventory is persisted first (edits aren't lost; the stored inventory
   * stays the single source of truth for the summary view), then the job runs with an
   * inline-inventory subset — bypassing stored-inventory lookup. registeredServiceId
   * is passed explicitly because inline jobs skip the inventory's provider preference.
   */
  const doMaskSubset = async (tableKeys, label, name) => {
    setSubmitting(true);
    try {
      await persistInventory(name);
      const subset = buildInlineFileInventory(selectedFieldsRef.current, tableKeys, fileTableMetaRef.current);
      if (!subset) {
        setNotification({ header: 'Nothing to mask', body: `No fields selected for ${label}` });
        return;
      }
      // Same pre-run connection gate as the full-inventory mask (after persist).
      const gate = await ensureConnectionTested(dataSourceId, {
        history,
        setNotification,
        name: connection,
      });
      if (!gate.ok) return;
      await startMaskingJob({
        dataSourceId,
        inventoryJson: subset,
        ...(selectedProvider ? { registeredServiceId: selectedProvider } : {}),
      });
      setNotification({
        header: 'Running masking',
        body: `Started masking job for ${label}`,
      });
      // Job started: land on the Secure Data Landscape, not the legacy masking-sources list.
      history.push('/secure');
    } catch (e) {
      setNotification({ header: 'Masking failed', body: apiErrorMessage(e) });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * READ-ONLY compare of the staged fields with a target, staged in the editor only: report →
   * banner, gap rows greyed out (reversibly — the pre-overlay fields are snapshotted). Nothing
   * is persisted; the trimmed `target_<name>` inventory is derived on Save / Mask.
   */
  const compareWithTarget = async (targetId, baseFields) => {
    setDeriving(true);
    try {
      // A previous target's overlay is undone first so the compare sees the full inventory.
      const fields = restoreTargetMissing(baseFields, preTargetSnapshotRef.current);
      const content = selectedFieldsToInventory(fields, connection, 'ui-user', {
        fileSource: isFileSource,
        tableMeta: fileTableMetaRef.current,
      });
      const cmp = await compareInventoryTargets({
        dataSourceId,
        inventoryJson: content,
        targetDataSourceIds: [targetId],
      });
      const report = cmp && Array.isArray(cmp.reports) ? cmp.reports[0] || null : null;
      setTargetDataSourceId(String(targetId));
      setTargetReport(report);
      preTargetSnapshotRef.current = fields;
      derivedTableExtrasRef.current = report ? tableExtrasFromReport(report) : {};
      setSelectedFields(report ? applyTargetGaps(fields, report) : fields);
    } catch (e) {
      setNotification({ header: 'Target comparison failed', body: apiErrorMessage(e) });
    } finally {
      setDeriving(false);
    }
  };

  /**
   * Mask-target picker. '' = in place: drop the report, restore any greyed-out preview rows, and
   * return to the root inventory if a derived one is open. A target: compare-only preview — the
   * `target_<name>` inventory is NOT saved until the user clicks Save or Mask. Works on drafts
   * too (the compare sends the staged fields inline, so no name is needed yet).
   */
  const handleTargetChange = async (value) => {
    if (value === '') {
      setTargetDataSourceId('');
      setTargetReport(null);
      if (currentIsDerived) {
        const rootExists = (inventories || []).some((i) => i && i.id === currentInventory.derivedFromInventoryId);
        setSelectedInventoryId(rootExists ? currentInventory.derivedFromInventoryId : null);
      } else {
        const snapshot = preTargetSnapshotRef.current;
        setSelectedFields((prev) => restoreTargetMissing(prev, snapshot));
        derivedTableExtrasRef.current = {};
      }
      preTargetSnapshotRef.current = null;
      return;
    }
    const targetId = Number(value);
    if (currentIsDerived) {
      // Editing a saved `target_<name>` copy: its own target is already loaded; a different
      // target means going back to the root inventory and previewing there after it loads.
      if (targetId === currentInventory.targetDataSourceId) return;
      pendingTargetRef.current = targetId;
      const rootExists = (inventories || []).some((i) => i && i.id === currentInventory.derivedFromInventoryId);
      setSelectedInventoryId(rootExists ? currentInventory.derivedFromInventoryId : null);
      return;
    }
    await compareWithTarget(targetId, selectedFieldsRef.current);
  };

  /**
   * Re-run the target comparison for the CURRENT inventory after its target's Profile finished.
   * A root inventory re-previews through the picker path; a derived `target_<name>` inventory
   * (whose target is fixed — handleTargetChange is a no-op for it) re-compares its root against
   * the target and re-applies the gap overlay to the staged fields in place, the same way the
   * load path does, so edits made while the banner waited are kept.
   */
  const recompareTarget = async (targetId) => {
    if (!currentIsDerived) {
      await handleTargetChange(String(targetId));
      return;
    }
    setDeriving(true);
    try {
      const cmp = await compareInventoryTargets({
        dataSourceId,
        inventoryId: currentInventory.derivedFromInventoryId,
        targetDataSourceIds: [targetId],
      });
      const report = cmp && Array.isArray(cmp.reports) ? cmp.reports[0] || null : null;
      setTargetReport(report);
      if (report) {
        const fromReport = tableExtrasFromReport(report);
        if (Object.keys(fromReport).length > 0) derivedTableExtrasRef.current = fromReport;
        setSelectedFields((prev) => applyTargetGaps(prev, report));
      }
    } catch (e) {
      setNotification({ header: 'Target comparison failed', body: apiErrorMessage(e) });
    } finally {
      setDeriving(false);
    }
  };

  // While the target's Profile runs (report.profilePending), poll the profile job and re-compare
  // once it SUCCEEDS — the server compares against the fresh snapshot. A profile that ends
  // FAILED / CANCELLED / INTERRUPTED is surfaced as such and NOT re-compared automatically: the
  // server starts a new Profile on every compare with no fresh snapshot, so re-comparing would
  // loop (a new failing job every poll). The user retries from the banner instead.
  useEffect(() => {
    if (!targetReport || !targetReport.profilePending || targetReport.profileJobId == null) return undefined;
    let cancelled = false;
    let timer = null;
    const jobId = targetReport.profileJobId;
    const tId = targetReport.targetDataSourceId;
    const tick = async () => {
      try {
        const job = await getProfileJob(jobId);
        if (cancelled) return;
        const status = String((job && job.status) || '').toUpperCase();
        if (job && TERMINAL_JOB_STATUSES.has(status)) {
          if (status === 'SUCCEEDED') {
            await recompareTarget(tId);
          } else {
            setTargetReport({
              ...targetReport,
              profilePending: false,
              profileFailed: true,
              profileStatus: status,
              profileError: profileJobError(job),
            });
          }
          return;
        }
      } catch (pollErr) {
        // keep polling — a transient error should not strand the banner
      }
      if (!cancelled) timer = setTimeout(tick, PROFILE_POLL_MS);
    };
    timer = setTimeout(tick, PROFILE_POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetReport]);

  // Save/Mask on an unsaved draft first collects a name for the new inventory.
  // `action` is 'save' | 'mask' | { kind: 'maskSubset', tableKeys, label }.
  const runAction = (action, name) => {
    if (action && action.kind === 'maskSubset') {
      return doMaskSubset(action.tableKeys, action.label, name);
    }
    if (action === 'mask') return doMask(name);
    return doSave(name);
  };

  const runOrPromptName = (action) => {
    if (selectedInventoryId == null) {
      setNameModal({ open: true, pendingAction: action });
      return;
    }
    runAction(action);
  };

  const handleRename = async (name) => {
    if (selectedInventoryId == null) return;
    try {
      await updateInventory(selectedInventoryId, { name });
      await refreshInventories();
    } catch (e) {
      setNotification({ header: 'Rename failed', body: e?.message || String(e) });
    }
  };

  const handleDelete = async () => {
    if (selectedInventoryId == null) return;
    try {
      await deleteInventory(selectedInventoryId);
      const list = await refreshInventories();
      const latest = latestInventoryOf(list);
      setSelectedInventoryId(latest ? latest.id : null);
      setNotification({ header: 'Inventory deleted', body: `Deleted masking inventory for ${connection}` });
    } catch (e) {
      setNotification({ header: 'Delete failed', body: e?.message || String(e) });
    }
  };

  // Download the on-screen field grid (including unsaved edits) as CSV or JSON.
  const handleExport = (format) => {
    const inv =
      selectedInventoryId != null
        ? (inventories || []).find((i) => i && i.id === selectedInventoryId)
        : null;
    const baseName = (inv && inv.name) || (connection ? `${connection} masking` : 'inventory');
    const exported = exportInventoryFields(selectedFields, format, baseName);
    if (exported === 0) {
      setNotification({ header: 'Nothing to export', body: 'This inventory has no fields.' });
    }
  };

  const inventorySelector = (
    <InventorySelector
      inventories={inventories}
      selectedId={selectedInventoryId === undefined ? null : selectedInventoryId}
      onSelect={(id) => {
        setSelectedInventoryId(id);
        pendingTargetRef.current = null;
        // Picking a root inventory by hand drops the target: the derived copy is what carries a
        // target; a derived pick re-selects its target in the load effect.
        const picked = (inventories || []).find((i) => i && i.id === id);
        if (!isDerivedInventory(picked)) {
          setTargetDataSourceId('');
          setTargetReport(null);
        }
      }}
      describe={(inv) =>
        isDerivedInventory(inv)
          ? `${inv.name || `Inventory ${inv.id}`} (target: ${inv.targetDataSourceName || targetNameOf(inv.targetDataSourceId)})`
          : null
      }
      onNew={readOnly ? undefined : () => setSelectedInventoryId(null)}
      onRename={readOnly ? undefined : handleRename}
      onDelete={readOnly ? undefined : handleDelete}
      disabled={submitting || fetchingData}
    />
  );

  return (
    <div className='inv-page-root'>
      <PageHeader
        header='Masking → Inventory'
        icon={BsJournalBookmark}
        tooltip='Review tables and fields selected for masking. A data source can have multiple named inventories.'
        actions={[
          <Dropdown.Item
            key='change-provider'
            disabled={fetchingData || readOnly || !providers || providers.length < 2}
            onClick={() => setProviderDialog(true)}
            data-testid='inventory-change-provider'
          >
            <BsArrowRepeat className='mr-2' />
            Change Provider
          </Dropdown.Item>,
          <Dropdown.Item
            key='export-csv'
            disabled={fetchingData}
            onClick={() => handleExport('csv')}
            data-testid='inventory-export-csv'
          >
            <BsDownload className='mr-2' />
            Export CSV
          </Dropdown.Item>,
          <Dropdown.Item
            key='export-json'
            disabled={fetchingData}
            onClick={() => handleExport('json')}
            data-testid='inventory-export-json'
          >
            <BsDownload className='mr-2' />
            Export JSON
          </Dropdown.Item>,
        ]}
      />
      <Card>
        <FetchContainer error={error} fetchingData={fetchingData} minHeight='375px'>
          <div className='inv-page'>
            <InventoryTitle
              connection={connection}
              engine={engine}
              providers={providers}
              selectedProvider={selectedProvider}
              noDiscoverFields={noDiscoverFields}
              selectedCount={getSelectedFieldsCount(selectedFields)}
              countNoun='masking'
              inventorySelector={inventorySelector}
              healthcareTag={source?.healthcareTag === true}
              healthcareTagSource={source?.healthcareTagSource || ''}
            />
            {!isFileSource && <InventoryIssuesBanner issues={inventoryIssues} />}
            {!isFileSource && (
              <TargetComparisonBanner
                report={targetReport}
                onRetry={
                  targetReport && targetReport.profileFailed && !deriving
                    ? () => recompareTarget(targetReport.targetDataSourceId)
                    : undefined
                }
                targetName={targetDataSourceId !== '' ? targetNameOf(Number(targetDataSourceId)) : undefined}
                derivedInventoryName={
                  currentIsDerived
                    ? currentInventory.name
                    : targetDataSourceId !== ''
                    ? `target_${targetNameOf(Number(targetDataSourceId))}`
                    : undefined
                }
              />
            )}
            <div className='inv-scroll'>
              {isFileSource ? (
                <FileInventoryGroups
                  selectedFields={selectedFields}
                  setSelectedFields={setSelectedFields}
                  algorithms={algorithms}
                  readOnly={readOnly}
                  source={source}
                  tableMeta={fileTableMetaRef.current}
                  submitting={submitting}
                  onMaskFiles={(tableKeys, label) =>
                    runOrPromptName({ kind: 'maskSubset', tableKeys, label })
                  }
                />
              ) : (
                <InventoryFieldsAccordion
                  selectedFields={selectedFields}
                  setSelectedFields={setSelectedFields}
                  algorithms={algorithms}
                  readOnly={readOnly}
                  serverSearch={serverSearch}
                  nomenclature={nomenclature}
                />
              )}
            </div>
            <div className='inv-sticky-bottom'>
                {!readOnly && !isFileSource && (eligibleTargets.length > 0 || sourceIsReadOnly) && (
                  <div className='mb-2'>
                    <div className='d-flex align-items-center' style={{ gap: '0.75rem' }}>
                    <Form.Label className='mb-0 text-nowrap' style={{ fontWeight: 500 }}>
                      Mask target
                    </Form.Label>
                    <div style={{ minWidth: 280 }}>
                      <SourcePickerField
                        value={targetDataSourceId === '' ? null : targetDataSourceId}
                        onChange={(id) => handleTargetChange(id == null ? '' : String(id))}
                        restrict={targetRules}
                        // A read-only source cannot be masked in place: a target is required.
                        clearable={!sourceIsReadOnly}
                        emptyLabel={sourceIsReadOnly
                          ? 'Choose a target (in place is unavailable for a read-only source)'
                          : 'In place — this data source'}
                        disabled={submitting || deriving}
                        dialogTitle={`Mask ${(source && source.name) || 'this source'} into…`}
                        dialogSubtitle='Leave the target empty to mask in place.'
                        verb='Use as target'
                        testId='mask-target'
                      />
                    </div>
                    {deriving && (
                      <span className='sg-small' data-testid='mask-target-deriving'>
                        Comparing with target…
                      </span>
                    )}
                    {targetDataSourceId !== '' && (
                      <Form.Check
                        type='checkbox'
                        id='truncate-before-insert'
                        label='Truncate target tables before insert'
                        checked={truncateBeforeInsert}
                        onChange={(e) => setTruncateBeforeInsert(e.target.checked)}
                        disabled={submitting}
                        data-testid='mask-target-truncate'
                      />
                    )}
                    {targetDataSourceId !== '' && (
                      <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
                        Masked rows are written to the target; this source is read, not modified.
                      </span>
                    )}
                    </div>
                    {sourceIsReadOnly && targetDataSourceId === '' && (
                      <div
                        className='sg-small mt-1'
                        style={{ color: 'var(--ink-500)' }}
                        data-testid='mask-target-readonly-hint'
                      >
                        This data source is read only — choose a mask target to run a cross-source mask.
                        {eligibleTargets.length === 0 &&
                          ' No eligible targets found: add a writable data source on the same engine.'}
                      </div>
                    )}
                  </div>
                )}
                <div className='d-flex justify-content-between align-items-center'>
                  <div className='d-flex' style={{ gap: '0.5rem' }}>
                    {!readOnly && (
                      <Button
                        variant='outline-secondary'
                        onClick={handleUseAllRecommended}
                        disabled={submitting || !dataSourceId}
                      >
                        Accept all
                      </Button>
                    )}
                    {!readOnly && (
                      <Button
                        variant='outline-secondary'
                        onClick={handleClearAll}
                        disabled={submitting || !dataSourceId}
                        data-testid='clear-all-selections'
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                  <div className='d-flex'>
                    <Button
                      onClick={() => history.push('/secure/mask')}
                      className='mr-2'
                      variant='outline-secondary'
                    >
                      {readOnly ? 'BACK' : 'CANCEL'}
                    </Button>
                    {!readOnly && (
                      <Button
                        className='mr-2'
                        variant='outline-primary'
                        disabled={submitting || !dataSourceId}
                        onClick={() => runOrPromptName('save')}
                      >
                        Save
                      </Button>
                    )}
                    {!readOnly && (
                    <Button
                      disabled={submitting || deriving || !dataSourceId || (sourceIsReadOnly && targetDataSourceId === '')}
                      onClick={() => runOrPromptName('mask')}
                      variant='primary'
                      data-testid='inventory-mask-button'
                    >
                      Mask
                    </Button>
                    )}
                  </div>
                </div>
            </div>
          </div>
        </FetchContainer>
      </Card>
      <InventoryNameModal
        show={nameModal.open}
        title='Name this inventory'
        initialName={connection ? `${connection} masking` : ''}
        submitLabel={
          nameModal.pendingAction === 'mask' || nameModal.pendingAction?.kind === 'maskSubset'
            ? 'Save & Mask'
            : 'Save'
        }
        onSubmit={(name) => {
          const action = nameModal.pendingAction;
          setNameModal({ open: false, pendingAction: null });
          runAction(action, name);
        }}
        onCancel={() => setNameModal({ open: false, pendingAction: null })}
      />
      <ChangeProviderDialog
        show={providerDialog}
        providers={providers}
        selectedProvider={selectedProvider}
        onSubmit={handleProviderChange}
        onCancel={() => setProviderDialog(false)}
      />
    </div>
  );
}

export default InventoryPage;
